import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import {
  primaryTelephoneNumber,
  readTelephoneSubscribers,
  type MedcomTelephoneSubscriber,
} from '@/lib/patientStamkortFields';

export interface CurrentPatient {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  telephone_subscribers: MedcomTelephoneSubscriber[];
  clinic_id: string;
}

export const currentPatientKey = (patientId: string | null) =>
  ['portal', 'current-patient', patientId] as const;

/**
 * Fetch the current patient record for the active portal clinic.
 */
export function useCurrentPatient() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: currentPatientKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<CurrentPatient | null> => {
      if (!activePatientId) return null;

      const { data, error } = await supabase
        .from('patients')
        .select('id, first_name, last_name, email, phone, telephone_subscribers, clinic_id')
        .eq('id', activePatientId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return {
        id: data.id,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: primaryTelephoneNumber(data.telephone_subscribers, data.phone),
        telephone_subscribers: readTelephoneSubscribers(data.telephone_subscribers),
        clinic_id: data.clinic_id,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}
